import * as React from 'react';

import axios from 'axios';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';

import { Button } from 'stile-shared/src/components/2020/common/Button';
import {
  Form,
  FormField,
  FormFieldError,
  Input,
  StyledComboBox,
} from 'stile-shared/src/components/2020/common/Form';
import { Link } from 'stile-shared/src/components/2020/common/Link';
import { Text } from 'stile-shared/src/components/2020/common/Primitives';
import { Select } from 'stile-shared/src/components/2020/common/Select';
import type {
  StileApiInboundLeads,
  StileApiLeadType,
  StileInboundLeadAu,
} from 'stile-shared/src/types/services';
import { isBrowser } from 'stile-shared/src/utils/isBrowser';
import { tagEvent } from 'stile-shared/src/utils/tagEvent';
import { emailValidator, phoneValidator } from 'stile-shared/src/utils/validators';

import { roleSelectOptions, stateSelectOptions } from 'utils/form';

import { auSchools } from 'data/schools-au';

type FormData = Omit<StileInboundLeadAu['auContact'], 'referrer' | 'leadType'>;

type TrialFormProps = {
  onSubmissionSuccess: React.Dispatch<React.SetStateAction<boolean>> | (() => void);
  leadType?: StileApiLeadType;
};

const scrollToTop = () => {
  if (isBrowser()) {
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
  }
};

export function TrialFormAu({ onSubmissionSuccess, leadType = 'GENERAL_CONTACT' }: TrialFormProps) {
  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
  } = useForm<FormData>();

  const {
    mutate,
    isLoading,
    reset: resetMutation,
    isSuccess,
    isError,
  } = useMutation(createEnquiry, {
    onSuccess: () => {
      tagEvent('trial_form_submit_au');
      resetForm();
      onSubmissionSuccess(true);
      setTimeout(scrollToTop, 300);
    },
    retry: 5,
  });

  const onSubmit = (formData: FormData) => {
    const submissionData: StileInboundLeadAu = {
      auContact: {
        ...formData,
        referrer: window.location.href,
        leadType: leadType,
      },
    };
    mutate(submissionData);
  };

  return isSuccess ? (
    <Text>
      One of our team members will be in touch shortly. In the meantime, take a look at the{' '}
      <Link url="http://www.stileapp.com/library">Stile Lesson Library</Link>!
    </Text>
  ) : (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FormField>
        <Input
          {...register('firstName', {
            maxLength: { value: 100, message: '100 character limit' },
            required: 'This field is required',
          })}
          label="First name*"
          placeholder="Fay"
          aria-invalid={errors.firstName ? 'true' : 'false'}
        />
        <FormFieldError name="firstName" errors={errors} />
      </FormField>

      <FormField>
        <Input
          label="Last name*"
          placeholder="James"
          aria-invalid={errors.lastName ? 'true' : 'false'}
          {...register('lastName', {
            maxLength: { value: 100, message: '100 character limit' },
            required: 'This field is required',
          })}
        />
        <FormFieldError name="lastName" errors={errors} />
      </FormField>

      <FormField>
        <label htmlFor="school-input">School name*</label>
        <StyledComboBox
          ariaLabel="school-input"
          name="school"
          placeholder="Port Niranda High School"
          options={auSchools}
          aria-invalid={errors.school ? 'true' : 'false'}
          register={register}
          validationOptions={{
            maxLength: { value: 200, message: '200 character limit' },
            required: 'This field is required',
          }}
        />
      </FormField>

      <FormField>
        <label htmlFor="state-select">State*</label>
        <Select
          id="state-select"
          options={stateSelectOptions}
          aria-invalid={errors.state ? 'true' : 'false'}
          {...register('state', { required: 'This field is required' })}
        />
      </FormField>

      <FormField>
        <label htmlFor="role-select">Role*</label>
        <Select
          id="role-select"
          options={roleSelectOptions}
          aria-invalid={errors.state ? 'true' : 'false'}
          {...register('role', { required: 'This field is required' })}
        />
      </FormField>

      <FormField>
        <Input
          type="email"
          label="Email*"
          aria-invalid={errors.email ? 'true' : 'false'}
          placeholder="fay.james@yourschool.vic.edu.au"
          {...register('email', {
            maxLength: { value: 200, message: '200 character limit' },
            required: 'This field is required',
            pattern: {
              value: emailValidator,
              message: 'Email address invalid',
            },
          })}
        />
        <FormFieldError name="email" errors={errors} />
      </FormField>

      <FormField>
        <Input
          type="tel"
          label="Phone*"
          placeholder="0402 123 123"
          aria-invalid={errors.phone ? 'true' : 'false'}
          {...register('phone', {
            required: 'This field is required',
            pattern: {
              value: phoneValidator,
              message: 'Phone number invalid',
            },
          })}
        />
        <FormFieldError name="phone" errors={errors} />
      </FormField>

      <FormField>
        <Input
          label="Anything in particular you'd like to learn more about?"
          {...register('moreInfoRequest', {
            maxLength: { value: 300, message: '300 character limit' },
          })}
        />
      </FormField>

      {isError && (
        <Text color="error">
          Something went wrong submitting your details, please try again and if the issue persists{' '}
          <Link variant="inherit" url="/who-we-are/contact/">
            get in touch with us
          </Link>
          .
        </Text>
      )}

      <Button type="submit" onClick={resetMutation} disabled={isLoading}>
        {isLoading ? 'Submitting...' : 'Submit'}
      </Button>
    </Form>
  );
}

const createEnquiry = async (data: StileApiInboundLeads) => {
  const config = {
    headers: { 'Content-Type': 'application/json' },
  };

  const { data: response } = await axios.post(
    'https://stileapp.com/api/au/v3/inboundLeads',
    data,
    config
  );

  return response.data;
};
